/**
 * useTemplateLabels — resolve the presentation vocabulary for a project's
 * canonical entity types (the FTL template-layer SKIN).
 *
 * MECHANISM: the backend template layer resolves a per-project-type manifest
 * that RELABELS the fixed canonical entity types for display (see
 * `templateLabelsApi` / `guardrail_resolver.py`). This hook fetches that
 * `labels` overlay and exposes a single `label(canonicalType, { plural })`
 * resolver used wherever an entity-type name is shown to the user.
 *
 * Resolution order for `label(type)`:
 *   1. The manifest overlay label for `type`, when present (e.g.
 *      "objective" → "Campaign Goal" for a marketing project).
 *   2. A deterministic humanized fallback of the canonical slug
 *      (e.g. "user_story" → "Story", "user_persona" → "Persona").
 *
 * The fallback is what guarantees the ZERO-VISIBLE-CHANGE contract: a project
 * with an empty overlay (every existing software/general project) renders the
 * EXACT canonical labels the surface shows today. The fallback singular/plural
 * table is aligned with the Iterator swim-lane vocabulary (LANE_LABELS /
 * TYPE_PLURAL in useForceLayout.ts) so there is no drift between the skinned
 * label and the un-skinned label for a non-marketing project.
 *
 * Caching: react-query keyed on the projectId, `staleTime` long because a
 * project's template type effectively never changes within a session. The
 * query is disabled when no projectId is present; the resolver then always
 * takes the humanized-fallback path.
 */

import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";

import {
  getTemplateLabels,
  type TemplateLabelMap,
  type TemplateLabelPair,
  type TemplateLabelsResponse,
} from "@/api/templateLabelsApi";

// ---------------------------------------------------------------------------
// The last overlay this browser saw for a project (row e5cc6e4b, measured on
// production 2026-10-12): during a fast walk the overlay request for a page
// can still be in flight, or be cancelled, when the page paints, and with no
// overlay every label fell back to the canonical words. The finance Engagement
// Tasks page then read "Tasks", "New Task", "Related: Components, Tests" under
// a nav saying "Engagement Tasks" and "Review Controls": two vocabularies on
// one screen. The last overlay seen is the right answer while the fresh one
// loads (a project's type does not change within a session), so it is the
// query's placeholder, and a transient failure is retried, never final.
// ---------------------------------------------------------------------------

const CACHE_PREFIX = "ftl-template-labels:";

/** The overlay last fetched for `projectId`, or undefined. Never throws. */
export function readCachedTemplateLabels(
  projectId: string,
): TemplateLabelsResponse | undefined {
  try {
    const raw = window.localStorage.getItem(CACHE_PREFIX + projectId);
    if (!raw) return undefined;
    const parsed = JSON.parse(raw) as TemplateLabelsResponse;
    return parsed && typeof parsed === "object" && parsed.labels ? parsed : undefined;
  } catch {
    return undefined;
  }
}

/** Remember the overlay for `projectId`. Never throws (storage may be off). */
export function writeCachedTemplateLabels(
  projectId: string,
  value: TemplateLabelsResponse,
): void {
  try {
    window.localStorage.setItem(CACHE_PREFIX + projectId, JSON.stringify(value));
  } catch {
    /* private mode or quota: the live fetch still answers */
  }
}

/** Retry a transient failure twice; a 4xx (no overlay, no access) is final. */
export function retryTemplateLabels(failureCount: number, error: unknown): boolean {
  // Read the status off the error rather than `instanceof ApiRequestError`:
  // a test that mocks "@/api/client" without that export made the class
  // lookup itself throw inside React Query's retry (vitest shard 3, #4021).
  const status = (error as { status?: unknown } | null)?.status;
  if (typeof status === "number" && status >= 400 && status < 500) {
    return false;
  }
  return failureCount < 2;
}

/** Options for `label(type, opts)`. */
export interface LabelOptions {
  /** Return the plural form (e.g. "Stories" instead of "Story"). */
  plural?: boolean;
  /**
   * Per-call fallback override — the EXACT word this surface shows today for a
   * NON-manifest project. Consulted ONLY when the project has no manifest
   * relabel for `canonicalType` (a manifest label ALWAYS wins, so a marketing
   * project still reads "Channels" everywhere). This is the zero-drift lever:
   * the same canonical type legitimately reads different words on different
   * surfaces for a plain project — the Iterator count-badge says "stories"
   * while the Orchestrator nav/page says "User Stories" — and each surface
   * passes its own current word here so the skin never changes what a
   * non-template user sees.
   */
  fallback?: string;
}

/** Signature of the resolver returned by the hook. */
export type LabelResolver = (
  canonicalType: string,
  opts?: LabelOptions,
) => string;

// ---------------------------------------------------------------------------
// Humanized canonical fallbacks — the EXACT vocabulary the un-skinned Iterator
// surface shows today. The PLURAL table is kept in lock-step with LANE_LABELS /
// TYPE_PLURAL in useForceLayout.ts (lane labels + count-badge plurals), so a
// non-marketing project's lanes/badges are byte-identical to the un-skinned map.
//
// The SINGULAR table is the per-card eyebrow word. Its ONLY consumer is the
// EntityNode eyebrow (BuildCanvas passes `label(n.type)`), whose pre-skin value
// was the humanized canonical slug (`data.type.replace(/_/g, " ")`, uppercased
// by the eyebrow CSS). To preserve the ZERO-VISIBLE-CHANGE contract for a
// non-marketing / empty-overlay card, each singular fallback is the humanized
// slug — byte-identical to that old eyebrow word once uppercased (e.g.
// user_story → "User Story", component → "Component", not "Story"/"Screen").
// Any type NOT in this table falls through to the generic slug-humanizer below,
// which produces the same word — so the table is a stable-word cache, not a
// relabel. Both naming variants (user_story/story, task/impl_task) resolve to a
// slug word so banding never silently disagrees.
// ---------------------------------------------------------------------------

const SINGULAR_FALLBACK: Record<string, string> = {
  objective: "Objective",
  concept: "Concept",
  user_story: "User Story",
  story: "Story",
  test_case: "Test Case",
  task: "Task",
  impl_task: "Impl Task",
  defect: "Defect",
  module: "Module",
  component: "Component",
  user_persona: "User Persona",
  change_request: "Change Request",
  risk: "Risk",
  decision: "Decision",
  document: "Document",
};

const PLURAL_FALLBACK: Record<string, string> = {
  objective: "Objectives",
  concept: "Concepts",
  user_story: "Stories",
  story: "Stories",
  test_case: "Tests",
  task: "Tasks",
  impl_task: "Tasks",
  defect: "Defects",
  module: "Modules",
  component: "Screens",
  user_persona: "Personas",
  change_request: "Changes",
  risk: "Risks",
  decision: "Decisions",
  document: "Documents",
};

/** Title-case a snake/space slug ("audience_segment" → "Audience Segment"). */
function humanizeSlug(slug: string): string {
  return slug
    .replace(/_/g, " ")
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");
}

/** Naive pluralizer for a manifest/humanized label that has no known plural. */
function naivePlural(word: string): string {
  if (!word) return word;
  if (/[sxz]$/i.test(word) || /(ch|sh)$/i.test(word)) return `${word}es`;
  if (/[^aeiou]y$/i.test(word)) return `${word.slice(0, -1)}ies`;
  return `${word}s`;
}

/**
 * Normalize one overlay entry to a `{singular, plural}` pair.
 *
 * The backend wire shape is the object form (`guardrail_resolver.labels()`
 * always emits `{singular, plural}`), but we also accept a flat string so a
 * hand-authored/legacy overlay that gives only the singular still works — the
 * plural is then derived naively. Returns null for a blank/invalid entry (the
 * caller falls back to the canonical vocabulary), which is what keeps a
 * malformed overlay from ever rendering `[object Object]`.
 */
function normalizeLabelPair(
  value: TemplateLabelPair | string | undefined | null,
): TemplateLabelPair | null {
  if (!value) return null;
  if (typeof value === "string") {
    const singular = value.trim();
    return singular ? { singular, plural: naivePlural(singular) } : null;
  }
  if (typeof value === "object") {
    const singular =
      typeof value.singular === "string" ? value.singular.trim() : "";
    if (!singular) return null;
    const plural =
      typeof value.plural === "string" && value.plural.trim()
        ? value.plural.trim()
        : naivePlural(singular);
    return { singular, plural };
  }
  return null;
}

/**
 * Build a pure `label()` resolver from a (possibly empty) manifest overlay.
 * Exported for direct unit testing without mounting react-query.
 */
export function makeLabelResolver(
  labels: TemplateLabelMap | Record<string, string> | undefined | null,
): LabelResolver {
  const overlay = (labels ?? {}) as Record<
    string,
    TemplateLabelPair | string
  >;
  return (canonicalType, opts) => {
    const plural = opts?.plural ?? false;
    // The overlay carries the authored {singular, plural} pair from the
    // manifest; use its plural directly (NOT a naive re-pluralization of the
    // singular) so "Campaign Goal" → "Campaign Goals" is the author's word.
    // A manifest relabel ALWAYS wins, ahead of any surface fallback — so a
    // marketing project reads "Channels" whether the callsite passed a
    // per-surface fallback or not.
    const skinned = normalizeLabelPair(overlay[canonicalType]);
    if (skinned) {
      return plural ? skinned.plural : skinned.singular;
    }
    // No manifest relabel: prefer the surface's own current word (opts.fallback)
    // so a NON-template project renders byte-identical to today. This is what
    // lets the Orchestrator nav say "User Stories" while the Iterator badge
    // says "Stories" for the SAME canonical type without either drifting.
    const surfaceFallback = opts?.fallback?.trim();
    if (surfaceFallback) return surfaceFallback;
    // Otherwise the built-in canonical vocabulary the un-skinned surface shows.
    const table = plural ? PLURAL_FALLBACK : SINGULAR_FALLBACK;
    const known = table[canonicalType];
    if (known) return known;
    const human = humanizeSlug(canonicalType);
    return plural ? naivePlural(human) : human;
  };
}

export interface UseTemplateLabelsResult {
  /** Resolve a canonical entity type to its display label. */
  label: LabelResolver;
  /** The resolved project type ("general" when no manifest applies). */
  projectType: string | null;
  /** The raw overlay (empty `{}` for non-marketing projects). */
  labels: TemplateLabelMap;
  /** True once the overlay has loaded (or the query is disabled). */
  isReady: boolean;
  /** Project route slugs this project type does not use (row 4aa3e0ec).
   *  `[]` for software/general projects and while loading. */
  hiddenSurfaces: string[];
}

const NO_HIDDEN_SURFACES: string[] = [];

/**
 * useTemplateLabels — react-query hook exposing the project's label resolver.
 *
 * When `projectId` is absent the query is disabled and `label()` always uses
 * the humanized canonical fallback (so callers can mount it unconditionally).
 */
export function useTemplateLabels(
  projectId: string | null | undefined,
): UseTemplateLabelsResult {
  const enabled = Boolean(projectId);

  const { data, isSuccess, isError, failureCount, isPlaceholderData } = useQuery({
    queryKey: ["template-labels", projectId],
    queryFn: async () => {
      const fresh = await getTemplateLabels(projectId as string);
      writeCachedTemplateLabels(projectId as string, fresh);
      return fresh;
    },
    enabled,
    // A project's template type does not change within a session; cache it
    // aggressively so the skin resolves once and every surface reuses it.
    staleTime: 5 * 60_000,
    gcTime: 30 * 60_000,
    // While the fresh overlay loads, the last one seen for this project
    // (row e5cc6e4b): never the canonical words on a skinned project.
    placeholderData: () => (projectId ? readCachedTemplateLabels(projectId) : undefined),
    // A missing/404 overlay is the SAFE default (no skin) and is final; a
    // transient failure (network, 5xx, a cancelled request) is retried twice
    // rather than leaving the page in the canonical words for the session.
    retry: retryTemplateLabels,
  });

  const labels = data?.labels ?? {};
  const projectType = data?.project_type ?? null;
  const rawHidden = data?.hidden_surfaces;
  const hiddenSurfaces = Array.isArray(rawHidden) ? rawHidden : NO_HIDDEN_SURFACES;

  const label = useMemo<LabelResolver>(
    () => makeLabelResolver(labels),
    // Re-derive only when the overlay identity changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [JSON.stringify(labels)],
  );

  return {
    label,
    projectType,
    labels,
    // "Ready" = we have an answer (success), we know we never will (error /
    // fallback), or the query is intentionally disabled. The resolver is
    // usable in every one of those states. Also ready on the last overlay
    // seen (placeholder) or after the first failed attempt: the retries
    // upgrade the labels in place and must never hold a page that waits on
    // isReady (ProjectLanding's mode cards waited ~3 s through the backoff).
    isReady: !enabled || isSuccess || isError || isPlaceholderData || failureCount > 0,
    hiddenSurfaces,
  };
}

export default useTemplateLabels;
